import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { createObjectifAutoEvaluationRequest, createObjectifNMoins1Request, fetchObjectifModificationRequestsRequest, fetchObjectifsNMoins1StepRequest, submitObjectifModificationRequest } from "../../../../redux/actions/performanceActions";
import { performanceApi } from "../../../../redux/api/performanceApi";
import { asArray, Field, StepState } from "../performanceUi";
import ObjectifNCreationForm from "./ObjectifNCreationForm";
import "./ObjectifsNStep.css";

const isExpired = (dateFin) => {
    if (!dateFin) return false;
    const deadline = new Date(dateFin);
    deadline.setHours(23, 59, 59, 999);
    return new Date() > deadline;
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("fr-FR");
};

const valueOrDash = (value, suffix = "") => value === undefined || value === null || value === "" ? "-" : `${value}${suffix}`;

const requestStatusLabel = (status) => {
    if (status === "SOUMISE") return "En attente";
    if (status === "APPROUVEE") return "Approuvee";
    if (status === "REFUSEE") return "Refusee";
    if (status === "ANNULEE") return "Annulee";
    return status || "Demande";
};

const fieldLabels = {
    titre: "Titre",
    description: "Description",
    indicateur: "Indicateur",
    typeIndicateur: "Type d'indicateur",
    performanceCible: "Performance cible",
    surperformance: "Surperformance",
    tolerance: "Tolerance",
    poids: "Poids",
    dateFin: "Date echeance",
};

const requestFields = [
    ["titre", "oldTitre"],
    ["indicateur", "oldIndicateur"],
    ["typeIndicateur", "oldTypeIndicateur"],
    ["performanceCible", "oldPerformanceCible"],
    ["surperformance", "oldSurperformance"],
    ["tolerance", "oldTolerance"],
    ["poids", "oldPoids"],
    ["dateFin", "oldDateFin"],
];

const parseJson = (value) => {
    if (!value) return {};
    try {
        return JSON.parse(value);
    } catch {
        return {};
    }
};

export default function ObjectifsNMoins1Step({ progressId, revisionMode = false, readOnly = false }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.objectifsNMoins1);
    const data = slice.dataByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const objectifs = asArray(data?.objectifs);
    const revisions = asArray(data?.revisions);
    const modificationHistory = useSelector((state) => state.performance.modificationsObjectifs.byObjectifId);
    const [drafts, setDrafts] = useState({});
    const [requestForms, setRequestForms] = useState({});
    const [openPanels, setOpenPanels] = useState({});
    const [directHistoryByObjectifId, setDirectHistoryByObjectifId] = useState({});
    const [addOpen, setAddOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchObjectifsNMoins1StepRequest(progressId));
    }, [dispatch, progressId]);

    useEffect(() => {
        objectifs.forEach((objectif) => {
            if (objectif.id) dispatch(fetchObjectifModificationRequestsRequest(objectif.id));
        });
    }, [dispatch, objectifs]);

    useEffect(() => {
        let mounted = true;
        const loadHistory = async () => {
            const entries = await Promise.all(objectifs.filter((objectif) => objectif.id).map(async (objectif) => {
                try {
                    const response = await performanceApi.fetchObjectifHistorique(objectif.id);
                    return [String(objectif.id), asArray(response?.data)];
                } catch {
                    return [String(objectif.id), []];
                }
            }));
            if (mounted) setDirectHistoryByObjectifId(Object.fromEntries(entries));
        };
        if (objectifs.length) loadHistory();
        return () => { mounted = false; };
    }, [objectifs]);

    const latestRevision = (objectifId, typeAction) => revisions.find((revision) => revision.objectifId === objectifId && revision.typeAction === typeAction);
    const requestHistoryFor = (objectif) => asArray(modificationHistory[String(objectif.id)]);
    const directHistoryFor = (objectif) => asArray(directHistoryByObjectifId[String(objectif.id)])
        .filter((item) => ["APPLICATION_MODIFICATION", "REJET"].includes(item.actionType));
    const historyCountFor = (objectif) => requestHistoryFor(objectif).length + directHistoryFor(objectif).length;
    const totalPoidsObjectifsNMoins1 = objectifs.reduce((sum, objectif) => sum + (Number(objectif.poids) || 0), 0);
    const canAddObjectif = totalPoidsObjectifsNMoins1 <= 100 && !readOnly;

    const setPanel = (objectifId, panel) => setOpenPanels((current) => ({
        ...current,
        [objectifId]: current[objectifId] === panel ? null : panel,
    }));

    const openRevisionPanel = (objectif, expired) => {
        if (objectif.approvalStatus !== "APPROVED") {
            Swal.fire({
                icon: "info",
                title: "Objectif en attente d'approbation",
                text: "Cet objectif doit etre approuve par le manager avant de pouvoir etre revise.",
                confirmButtonColor: "#1f4690",
            });
            return;
        }
        if (!expired) setPanel(objectif.id, "revision");
    };

    const updateDraft = (objectifId, patch) => setDrafts((current) => ({
        ...current,
        [objectifId]: { ...(current[objectifId] || {}), ...patch },
    }));

    const updateRequestForm = (objectifId, requestType, patch) => {
        const key = `${objectifId}-${requestType}`;
        setRequestForms((current) => ({ ...current, [key]: { ...(current[key] || {}), ...patch } }));
    };

    const getRequestForm = (objectifId, requestType) => requestForms[`${objectifId}-${requestType}`] || {};

    const submitAutoEval = (objectif) => {
        const draft = drafts[objectif.id] || {};
        dispatch(createObjectifAutoEvaluationRequest(objectif.id, {
            evaluationProgressId: Number(progressId),
            tauxRealisation: draft.tauxRealisation,
            commentaire: draft.commentaire,
        }));
    };

    const submitChangeRequest = (objectif, requestType) => {
        const form = getRequestForm(objectif.id, requestType);
        if (!String(form.justification || "").trim()) {
            window.alert("Veuillez remplir la justification.");
            return;
        }
        const payload = requestType === "MODIFICATION"
            ? {
                requestType,
                titre: form.titre,
                description: form.description,
                indicateur: form.indicateur,
                typeIndicateur: form.typeIndicateur,
                performanceCible: form.performanceCible,
                poids: form.poids,
                dateFin: form.dateFin,
                surperformance: form.surperformance,
                tolerance: form.tolerance,
                justification: form.justification,
            }
            : {
                requestType,
                performanceCible: form.performanceCible,
                poids: form.poids,
                dateFin: form.dateFin,
                surperformance: form.surperformance,
                tolerance: form.tolerance,
                justification: form.justification,
            };

        dispatch(submitObjectifModificationRequest(objectif.id, payload, progressId, true));
        setPanel(objectif.id, null);
    };

    const addObjectif = (payload) => {
        dispatch(createObjectifNMoins1Request(progressId, {
            ...payload,
            evaluationProgressId: Number(progressId),
        }));
        setAddOpen(false);
    };

    const renderHistory = (objectif) => {
        const requestHistory = requestHistoryFor(objectif);
        const directHistory = directHistoryFor(objectif);
        if (!requestHistory.length && !directHistory.length) return null;
        const requestDiffs = (request) => requestFields
            .filter(([field]) => request[field] !== undefined && request[field] !== null && request[field] !== "")
            .map(([field, oldField]) => ({ field, oldValue: request[oldField] ?? "-", newValue: request[field] }));
        const directDiffs = (item) => {
            const oldValues = parseJson(item.oldValueJson);
            const newValues = parseJson(item.newValueJson);
            return Array.from(new Set([...Object.keys(oldValues), ...Object.keys(newValues)]))
                .map((field) => ({ field, oldValue: oldValues[field] ?? "-", newValue: newValues[field] ?? "-" }));
        };
        return (
            <div className="on-request-history on-objectives-history">
                <div className="on-request-history__title">Historique des revisions/modifications</div>
                <div className="on-request-history__list">
                    {requestHistory.map((request) => (
                        <div className="on-request-history__item" key={request.id}>
                            <div className="on-request-history__head">
                                <span className="on-request-history__type">{request.requestType || "REVISION"}</span>
                                <span className={`on-status-badge on-status-badge--${String(request.status || "").toLowerCase()}`}>
                                    {requestStatusLabel(request.status)}
                                </span>
                            </div>
                            {request.justification && <p className="on-request-history__text">Justification: {request.justification}</p>}
                            {request.decisionComment && <p className="on-request-history__text">Commentaire manager: {request.decisionComment}</p>}
                            <div className="on-request-history__diffs">
                                {requestDiffs(request).map(({ field, oldValue, newValue }) => (
                                    <div className="on-request-history__diff" key={field}>
                                        <span>{fieldLabels[field] || field}</span>
                                        <strong>{valueOrDash(oldValue)} -&gt; {valueOrDash(newValue)}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    {directHistory.map((item) => (
                        <div className="on-request-history__item" key={`direct-${item.id}`}>
                            <div className="on-request-history__head">
                                <span className="on-request-history__type">{item.actionType === "REJET" ? "SUPPRESSION MANAGER" : "REVISION MANAGER"}</span>
                                <span className="on-status-badge on-status-badge--approuvee">Appliquee</span>
                            </div>
                            {item.createdAt && <p className="on-request-history__text">{new Date(item.createdAt).toLocaleString("fr-FR")}</p>}
                            {item.commentaire && <p className="on-request-history__text">{item.commentaire}</p>}
                            <div className="on-request-history__diffs">
                                {directDiffs(item).length === 0 && <p className="on-request-history__text">Details non disponibles pour cet historique.</p>}
                                {directDiffs(item).map(({ field, oldValue, newValue }) => (
                                    <div className="on-request-history__diff" key={field}>
                                        <span>{fieldLabels[field] || field}</span>
                                        <strong>{valueOrDash(oldValue)} -&gt; {valueOrDash(newValue)}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderDetails = (objectif, autoEvaluation, managerEvaluation) => (
        <div className="on-objectives-row-panel">
            <div className="on-facts">
                <div className="on-fact"><span className="on-fact__key">Indicateur</span><span className="on-fact__val">{objectif.indicateur || "-"}</span></div>
                <div className="on-fact"><span className="on-fact__key">Type indicateur</span><span className="on-fact__val">{objectif.typeIndicateur || "-"}</span></div>
                <div className="on-fact"><span className="on-fact__key">Auto-evaluation</span><span className="on-fact__val">{valueOrDash(autoEvaluation?.tauxRealisation, "%")}</span></div>
                <div className="on-fact"><span className="on-fact__key">Validation manager</span><span className="on-fact__val">{valueOrDash(managerEvaluation?.tauxRealisation, "%")}</span></div>
            </div>
            {objectif.description && <p className="on-item__desc">{objectif.description}</p>}
            {renderHistory(objectif)}
        </div>
    );

    const renderRevisionForm = (objectif) => {
        const form = getRequestForm(objectif.id, "REVISION");
        return (
            <div className="on-form on-request-form on-objectives-row-panel">
                <div className="on-form__section-label">Demande de revision Objectif N-1</div>
                <div className="on-form__row on-form__row--3">
                    <Field label="Performance cible"><input className="on-input" type="number" step="0.01" value={form.performanceCible ?? objectif.performanceCible ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { performanceCible: event.target.value })} /></Field>
                    <Field label="Poids"><input className="on-input" type="number" step="0.01" value={form.poids ?? objectif.poids ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { poids: event.target.value })} /></Field>
                    <Field label="Date echeance"><input className="on-input" type="date" value={form.dateFin ?? objectif.dateFin ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { dateFin: event.target.value })} /></Field>
                </div>
                <div className="on-form__row on-form__row--2">
                    <Field label="Surperformance"><input className="on-input" type="number" step="0.01" value={form.surperformance ?? objectif.surperformance ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { surperformance: event.target.value })} /></Field>
                    <Field label="Tolerance"><input className="on-input" type="number" step="0.01" value={form.tolerance ?? objectif.tolerance ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { tolerance: event.target.value })} /></Field>
                </div>
                <Field label="Justification"><input className="on-input" value={form.justification || ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { justification: event.target.value })} /></Field>
                <button className="on-btn on-btn--primary on-btn--compact" type="button" onClick={() => submitChangeRequest(objectif, "REVISION")}>Envoyer la revision</button>
            </div>
        );
    };

    const renderEvaluationForm = (objectif, autoEvaluation, managerEvaluation) => {
        const draft = drafts[objectif.id] || {};
        const locked = Boolean(managerEvaluation);
        const approved = objectif.approvalStatus === "APPROVED";
        const expired = isExpired(objectif.dateFin);
        return (
            <div className="on-form on-request-form on-objectives-row-panel">
                <div className="on-form__section-label">Evaluation Objectif N-1</div>
                {locked && <div className="perf-state perf-state--inline">Valide par le manager.</div>}
                {approved && !expired && !locked && <div className="perf-state perf-state--warning">Evaluation disponible apres la date d'echeance.</div>}
                <div className="on-form__row on-form__row--2">
                    <Field label="Taux realisation">
                        <input
                            className="on-input"
                            type="number"
                            min="0"
                            max="200"
                            disabled={readOnly || !approved || !expired || locked}
                            value={draft.tauxRealisation ?? autoEvaluation?.tauxRealisation ?? ""}
                            onChange={(event) => updateDraft(objectif.id, { tauxRealisation: event.target.value })}
                        />
                    </Field>
                    <Field label="Commentaire">
                        <input
                            className="on-input"
                            disabled={readOnly || !approved || !expired || locked}
                            value={draft.commentaire ?? autoEvaluation?.commentaire ?? ""}
                            onChange={(event) => updateDraft(objectif.id, { commentaire: event.target.value })}
                        />
                    </Field>
                </div>
                <button className="on-btn on-btn--primary on-btn--compact" type="button" disabled={readOnly || !approved || !expired || locked} onClick={() => submitAutoEval(objectif)}>Enregistrer l'evaluation</button>
            </div>
        );
    };

    const renderEvaluationCards = () => (
        <div className="perf-stack">
            {objectifs.map((objectif) => {
                const autoEvaluation = latestRevision(objectif.id, "AUTO_EVALUATION");
                const managerEvaluation = latestRevision(objectif.id, "MANAGER_EVALUATION");
                const expired = isExpired(objectif.dateFin);
                const approved = objectif.approvalStatus === "APPROVED";
                const activePanel = openPanels[objectif.id];
                const locked = Boolean(managerEvaluation);
                return (
                    <article className="perf-item" key={objectif.id}>
                        <div className="perf-item__head">
                            <h3>{objectif.titre}</h3>
                            <span className="perf-badge perf-badge--blue">{managerEvaluation ? "VALIDE_MANAGER" : (objectif.approvalStatus || objectif.statusObjectif || "OBJECTIF")}</span>
                        </div>
                        <p>{objectif.description || "Aucune description."}</p>
                        <dl className="perf-facts">
                            <div><dt>Cible</dt><dd>{valueOrDash(objectif.performanceCible)}</dd></div>
                            <div><dt>Surperformance</dt><dd>{valueOrDash(objectif.surperformance)}</dd></div>
                            <div><dt>Tolerance</dt><dd>{valueOrDash(objectif.tolerance)}</dd></div>
                            <div><dt>Poids</dt><dd>{valueOrDash(objectif.poids, "%")}</dd></div>
                            <div><dt>Auto-evaluation</dt><dd>{valueOrDash(autoEvaluation?.tauxRealisation, "%")}</dd></div>
                            <div><dt>Validation manager</dt><dd>{valueOrDash(managerEvaluation?.tauxRealisation, "%")}</dd></div>
                        </dl>
                        {locked && <div className="perf-state perf-state--inline">Valide par le manager.</div>}
                        {approved && !expired && !locked && <div className="perf-state perf-state--warning">Evaluation disponible apres la date d'echeance.</div>}
                        <div className="perf-actions">
                            <button className="perf-btn perf-btn--primary" type="button" disabled={readOnly || !approved || !expired || locked} onClick={() => setPanel(objectif.id, "evaluation")}>Evaluer</button>
                            <button className="perf-btn" type="button" disabled={!activePanel} onClick={() => setPanel(objectif.id, null)}>Annuler</button>
                        </div>
                        {activePanel === "evaluation" && renderEvaluationForm(objectif, autoEvaluation, managerEvaluation)}
                    </article>
                );
            })}
        </div>
    );

    return (
        <StepState loading={loading} error={error} empty={!data}>
            {data?.blockedReason && <div className="perf-state perf-state--warning">{data.blockedReason}</div>}
            {!data?.blockedReason && objectifs.length === 0 && <div className="perf-state">Aucun objectif N-1 disponible.</div>}

            <div className="on-objectives-toolbar">
                <button className="on-btn on-btn--primary" type="button" disabled={!canAddObjectif} title={!canAddObjectif ? "Le poids total des objectifs N-1 depasse 100%." : ""} onClick={() => setAddOpen(true)}>
                    + Ajouter un objectif
                </button>
            </div>

            {!revisionMode && objectifs.length > 0 && renderEvaluationCards()}

            {revisionMode && objectifs.length > 0 && (
            <div className="on-objectives-table-wrap">
                <table className="on-objectives-table">
                    <thead>
                    <tr>
                        <th>Titre</th>
                        <th>Performance (%)</th>
                        <th>Surperformance (%)</th>
                        <th>Tolerance (%)</th>
                        <th>Poids (%)</th>
                        <th>Date echeance</th>
                        <th>Taux realisation</th>
                        <th>Revisions</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {objectifs.map((objectif) => {
                        const autoEvaluation = latestRevision(objectif.id, "AUTO_EVALUATION");
                        const managerEvaluation = latestRevision(objectif.id, "MANAGER_EVALUATION");
                        const expired = isExpired(objectif.dateFin);
                        const approved = objectif.approvalStatus === "APPROVED";
                        const realization = managerEvaluation?.tauxRealisation ?? autoEvaluation?.tauxRealisation;
                        const activePanel = openPanels[objectif.id];
                        return (
                            <React.Fragment key={objectif.id}>
                                <tr>
                                    <td>
                                        <strong className="on-objectives-title">{objectif.titre || "-"}</strong>
                                        {objectif.description && <span className="on-objectives-description">{objectif.description}</span>}
                                    </td>
                                    <td>{valueOrDash(objectif.performanceCible)}</td>
                                    <td>{valueOrDash(objectif.surperformance)}</td>
                                    <td>{valueOrDash(objectif.tolerance)}</td>
                                    <td>{valueOrDash(objectif.poids)}</td>
                                    <td>{formatDate(objectif.dateFin)}</td>
                                    <td>
                                        <div className="on-objectives-realisation">
                                            <strong>{valueOrDash(realization, "%")}</strong>
                                            {managerEvaluation && <span>Valide manager</span>}
                                        </div>
                                    </td>
                                    <td>
                                        <button className="on-revision-count" type="button" onClick={() => setPanel(objectif.id, "details")}>
                                            {historyCountFor(objectif)}
                                        </button>
                                    </td>
                                    <td>
                                        <div className="on-objectives-actions">
                                            <button className="on-btn on-btn--outline on-btn--xs" type="button" onClick={() => setPanel(objectif.id, "details")}>Details</button>
                                            {revisionMode ? (
                                                <button
                                                    className={`on-btn on-btn--outline on-btn--xs on-btn--success ${!approved ? "on-btn--disabled-soft" : ""}`}
                                                    type="button"
                                                    disabled={readOnly || expired}
                                                    title={expired ? "La date d'échéance est dépassée." : (!approved ? "Objectif en attente d'approbation" : "")}
                                                    onClick={() => openRevisionPanel(objectif, expired)}
                                                >
                                                    Reviser
                                                </button>
                                            ) : (
                                                <button className="on-btn on-btn--outline on-btn--xs on-btn--success" type="button" disabled={readOnly || !approved || !expired || Boolean(managerEvaluation)} title={!expired ? "La date d'echeance n'est pas encore depassee." : ""} onClick={() => setPanel(objectif.id, "evaluation")}>Evaluer</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {activePanel && (
                                    <tr className="on-objectives-expanded-row">
                                        <td colSpan={9}>
                                            {activePanel === "details" && renderDetails(objectif, autoEvaluation, managerEvaluation)}
                                            {activePanel === "revision" && renderRevisionForm(objectif)}
                                            {activePanel === "evaluation" && renderEvaluationForm(objectif, autoEvaluation, managerEvaluation)}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            )}

            {addOpen && (
                <div className="on-card">
                    <ObjectifNCreationForm
                        title={`Nouvel objectif N-1${data?.campagneNMoins1Nom ? ` - ${data.campagneNMoins1Nom}` : ""}`}
                        submitLabel="Ajouter l'objectif N-1"
                        onSubmit={addObjectif}
                        onCancel={() => setAddOpen(false)}
                    />
                </div>
            )}
        </StepState>
    );
}
